import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Search, 
    Plus, 
    Minus, 
    AlertTriangle, 
    ArrowUpCircle, 
    ArrowDownCircle,
    Boxes,
    History,
    Loader2,
    DollarSign,
    ScanBarcode
} from 'lucide-react';
import { toast } from 'sonner';
import useBarcodeScanner from '../hooks/useBarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StockPage = () => {
    const [products, setProducts] = useState([]);
    const [movements, setMovements] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [movementType, setMovementType] = useState('entrada');
    const [quantity, setQuantity] = useState('');
    const [reason, setReason] = useState('');
    const [unitCost, setUnitCost] = useState('');
    const [saving, setSaving] = useState(false);
    const [scannerEnabled, setScannerEnabled] = useState(true);

    // Handler para leitura de código de barras no Estoque
    const handleBarcodeScan = useCallback(async (barcode) => {
        console.log('[ESTOQUE] Scanner detectou código:', barcode);
        console.log('[ESTOQUE] Caracteres do código:', barcode.split('').map(c => `${c}(${c.charCodeAt(0)})`).join(' '));
        
        // Limpar código - remover caracteres não numéricos se o código parecer ser EAN/UPC
        let cleanBarcode = barcode;
        
        // Se o código tem mais de 8 dígitos e começa com letra, pode ser erro do scanner
        if (barcode.length >= 8 && /^[A-Za-z]/.test(barcode)) {
            const numbersOnly = barcode.replace(/[^0-9]/g, '');
            if (numbersOnly.length >= 8) {
                console.log('[ESTOQUE] Código parece ter prefixo inválido, usando apenas números:', numbersOnly);
                cleanBarcode = numbersOnly;
            }
        }
        
        setScannerEnabled(false);
        
        try {
            // Buscar produto pelo código de barras
            console.log('[ESTOQUE] Buscando produto no backend com código:', cleanBarcode);
            const response = await axios.get(`${API_URL}/api/products/barcode/${cleanBarcode}`);
            const product = response.data;
            console.log('[ESTOQUE] Produto encontrado:', product.name);
            
            // Produto encontrado - abrir dialog de movimentação (entrada por padrão)
            toast.success(`Produto encontrado: ${product.name}`, {
                icon: '📦',
                duration: 2000
            });
            
            setSelectedProduct(product);
            setMovementType('entrada');
            setQuantity('');
            setReason('');
            setUnitCost('');
            setDialogOpen(true);
            
        } catch (error) {
            console.error('[ESTOQUE] Erro ao buscar produto:', error);
            if (error.response?.status === 404) {
                toast.error(`Produto não encontrado: ${barcode}`, {
                    description: cleanBarcode !== barcode 
                        ? `Código limpo: ${cleanBarcode} - Cadastre o produto primeiro` 
                        : 'Cadastre o produto primeiro na página de Produtos',
                    icon: '❌',
                    duration: 5000
                });
            } else {
                toast.error('Erro ao buscar produto');
            }
        } finally {
            setTimeout(() => setScannerEnabled(true), 1000);
        }
    }, []);

    // Hook do scanner - ativo apenas quando dialog está fechado
    const { isScanning } = useBarcodeScanner({
        enabled: scannerEnabled && !dialogOpen,
        onScan: handleBarcodeScan,
        minLength: 4,
        maxDelay: 150  // Aumentado para compatibilidade com scanners mais lentos
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [productsRes, movementsRes, lowStockRes] = await Promise.all([
                axios.get(`${API_URL}/api/products?active_only=false`),
                axios.get(`${API_URL}/api/stock/movements?limit=50`),
                axios.get(`${API_URL}/api/stock/low`)
            ]);
            setProducts(productsRes.data);
            setMovements(movementsRes.data);
            setLowStock(lowStockRes.data);
        } catch (error) {
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const openMovementDialog = (product, type) => {
        setSelectedProduct(product);
        setMovementType(type);
        setQuantity('');
        setReason('');
        setUnitCost('');
        setDialogOpen(true);
    };

    const handleMovement = async () => {
        if (!quantity || parseInt(quantity) <= 0) {
            toast.error('Quantidade inválida');
            return;
        }

        setSaving(true);

        try {
            const qty = parseInt(quantity);
            const payload = {
                product_id: selectedProduct.id,
                quantity: movementType === 'entrada' ? qty : -qty,
                movement_type: movementType,
                reason: reason || null
            };
            
            // Add unit_cost for entries with cost (compra)
            if (movementType === 'entrada' && unitCost && parseFloat(unitCost) > 0) {
                payload.unit_cost = parseFloat(unitCost);
            }
            
            const response = await axios.post(`${API_URL}/api/stock/movement`, payload);

            let successMsg = 'Movimentação registrada!';
            if (response.data.new_cost_price) {
                successMsg += ` Novo custo médio: R$${response.data.new_cost_price.toFixed(2)}`;
            }
            toast.success(successMsg);
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar movimentação');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-4 lg:space-y-6 animate-fade-in-up" data-testid="stock-page">
            {/* Scanner Indicator */}
            {isScanning && (
                <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse">
                    <ScanBarcode className="w-5 h-5" />
                    <span className="font-medium">Lendo código de barras...</span>
                </div>
            )}
            
            <div>
                <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Estoque</h1>
                <p className="text-gray-400 text-sm lg:text-base mt-1">
                    Controle de entrada e saída de produtos
                    <span className="ml-2 inline-flex items-center gap-1 text-red-400">
                        <ScanBarcode className="w-4 h-4" />
                        Scanner ativo
                    </span>
                </p>
            </div>

            <Tabs defaultValue="products" className="w-full">
                <TabsList className="bg-[#121212] border border-[#262626] w-full sm:w-auto flex overflow-x-auto">
                    <TabsTrigger value="products" className="flex-1 sm:flex-none text-xs sm:text-sm data-[state=active]:bg-rr-yellow-500 data-[state=active]:text-black">
                        <Boxes className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Produtos</span>
                    </TabsTrigger>
                    <TabsTrigger value="low-stock" className="flex-1 sm:flex-none text-xs sm:text-sm data-[state=active]:bg-rr-yellow-500 data-[state=active]:text-black">
                        <AlertTriangle className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Baixo</span> ({lowStock.length})
                    </TabsTrigger>
                    <TabsTrigger value="history" className="flex-1 sm:flex-none text-xs sm:text-sm data-[state=active]:bg-rr-yellow-500 data-[state=active]:text-black">
                        <History className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Histórico</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="products" className="mt-4 lg:mt-6">
                    <Card className="bg-[#121212] border-[#262626]">
                        <CardHeader className="p-3 lg:p-6">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-gray-500" />
                                <Input
                                    placeholder="Buscar produtos..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 lg:pl-11 bg-[#0a0a0a] border-[#262626] text-white h-10"
                                    data-testid="search-stock-input"
                                />
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 lg:p-6 lg:pt-0">
                            {/* Mobile Card View */}
                            <div className="lg:hidden space-y-2 p-3">
                                {filteredProducts.map(product => {
                                    const isLow = product.stock_quantity <= product.min_stock;
                                    return (
                                        <div 
                                            key={product.id}
                                            className="p-3 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]"
                                            data-testid={`stock-row-${product.id}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-white font-medium text-sm truncate">{product.name}</p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <Badge variant="outline" className="border-[#333] text-gray-400 text-[10px]">
                                                            {product.category}
                                                        </Badge>
                                                        {isLow ? (
                                                            <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30 text-[10px]">
                                                                Baixo
                                                            </Badge>
                                                        ) : (
                                                            <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 text-[10px]">
                                                                OK
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <span className={`text-xl font-bold ${isLow ? 'text-amber-500' : 'text-white'}`}>
                                                        {product.stock_quantity}
                                                    </span>
                                                    <p className="text-[10px] text-gray-500">mín: {product.min_stock}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 mt-2 pt-2 border-t border-[#1a1a1a]">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="flex-1 border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 h-8 text-xs"
                                                    onClick={() => openMovementDialog(product, 'entrada')}
                                                >
                                                    <Plus className="w-3 h-3 mr-1" />
                                                    Entrada
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="flex-1 border-red-500/30 text-red-500 hover:bg-red-500/10 h-8 text-xs"
                                                    onClick={() => openMovementDialog(product, 'saida')}
                                                >
                                                    <Minus className="w-3 h-3 mr-1" />
                                                    Saída
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Desktop Table View */}
                            <div className="hidden lg:block overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-[#262626] hover:bg-transparent">
                                        <TableHead className="text-gray-400">Produto</TableHead>
                                        <TableHead className="text-gray-400">Categoria</TableHead>
                                        <TableHead className="text-gray-400 text-center">Estoque Atual</TableHead>
                                        <TableHead className="text-gray-400 text-center">Mínimo</TableHead>
                                        <TableHead className="text-gray-400 text-center">Status</TableHead>
                                        <TableHead className="text-gray-400 text-right">Ações</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredProducts.map(product => {
                                        const isLow = product.stock_quantity <= product.min_stock;
                                        return (
                                            <TableRow 
                                                key={product.id} 
                                                className="border-[#262626] hover:bg-white/5"
                                                data-testid={`stock-row-${product.id}`}
                                            >
                                                <TableCell className="text-white font-medium">{product.name}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className="border-[#333] text-gray-400">
                                                        {product.category}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    <span className={`text-xl font-bold ${isLow ? 'text-amber-500' : 'text-white'}`}>
                                                        {product.stock_quantity}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-center text-gray-400">
                                                    {product.min_stock}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {isLow ? (
                                                        <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">
                                                            Baixo
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30">
                                                            OK
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10"
                                                            onClick={() => openMovementDialog(product, 'entrada')}
                                                            data-testid={`add-stock-${product.id}`}
                                                        >
                                                            <Plus className="w-4 h-4 mr-1" />
                                                            Entrada
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                                                            onClick={() => openMovementDialog(product, 'saida')}
                                                            data-testid={`remove-stock-${product.id}`}
                                                        >
                                                            <Minus className="w-4 h-4 mr-1" />
                                                            Saída
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="low-stock" className="mt-4 lg:mt-6">
                    <Card className="bg-[#121212] border-[#262626]">
                        <CardHeader className="p-3 lg:p-6">
                            <CardTitle className="text-white font-heading text-sm lg:text-base flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 lg:w-5 lg:h-5 text-amber-500" />
                                Produtos com Estoque Baixo
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 lg:p-6 pt-0">
                            {lowStock.length === 0 ? (
                                <div className="text-center py-8 lg:py-12 text-gray-500 text-sm">
                                    Todos os produtos estão com estoque adequado
                                </div>
                            ) : (
                                <div className="space-y-2 lg:space-y-3">
                                    {lowStock.map(product => (
                                        <div 
                                            key={product.id}
                                            className="flex items-center justify-between p-3 lg:p-4 bg-[#0a0a0a] rounded-md border border-amber-500/20"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-white font-medium text-sm lg:text-base truncate">{product.name}</p>
                                                <p className="text-xs lg:text-sm text-gray-500">{product.category}</p>
                                            </div>
                                            <div className="flex items-center gap-2 lg:gap-4">
                                                <div className="text-right">
                                                    <p className="text-amber-500 font-bold text-lg lg:text-xl">{product.stock_quantity}</p>
                                                    <p className="text-[10px] lg:text-xs text-gray-500">mín: {product.min_stock}</p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    className="bg-rr-yellow-500 text-black hover:bg-rr-yellow-400 h-8 text-xs lg:text-sm"
                                                    onClick={() => openMovementDialog(product, 'entrada')}
                                                >
                                                    <Plus className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
                                                    Repor
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="history" className="mt-6">
                    <Card className="bg-[#121212] border-[#262626]">
                        <CardHeader>
                            <CardTitle className="text-white font-heading flex items-center gap-2">
                                <History className="w-5 h-5 text-rr-yellow-500" />
                                Histórico de Movimentações
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ScrollArea className="h-[500px]">
                                <div className="space-y-3">
                                    {movements.map(movement => (
                                        <div 
                                            key={movement.id}
                                            className="flex items-center gap-4 p-4 bg-[#0a0a0a] rounded-md border border-[#1a1a1a]"
                                        >
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                movement.quantity > 0 
                                                    ? 'bg-emerald-500/10 text-emerald-500' 
                                                    : 'bg-red-500/10 text-red-500'
                                            }`}>
                                                {movement.quantity > 0 ? (
                                                    <ArrowUpCircle className="w-5 h-5" />
                                                ) : (
                                                    <ArrowDownCircle className="w-5 h-5" />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-white font-medium">{movement.product_name}</p>
                                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                                    <span>{movement.movement_type}</span>
                                                    {movement.reason && (
                                                        <>
                                                            <span>•</span>
                                                            <span>{movement.reason}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className={`font-bold ${movement.quantity > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                                    {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                                                </p>
                                                <p className="text-xs text-gray-500">{formatDate(movement.created_at)}</p>
                                            </div>
                                        </div>
                                    ))}
                                    {movements.length === 0 && (
                                        <div className="text-center py-12 text-gray-500">
                                            Nenhuma movimentação registrada
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Movement Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white" data-testid="movement-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            {movementType === 'entrada' ? (
                                <ArrowUpCircle className="w-6 h-6 text-emerald-500" />
                            ) : (
                                <ArrowDownCircle className="w-6 h-6 text-red-500" />
                            )}
                            {movementType === 'entrada' ? 'Entrada de Estoque' : 'Saída de Estoque'}
                        </DialogTitle>
                    </DialogHeader>

                    {selectedProduct && (
                        <div className="space-y-4 py-4">
                            <div className="p-4 bg-[#121212] rounded-md">
                                <p className="text-white font-medium">{selectedProduct.name}</p>
                                <div className="flex justify-between text-sm text-gray-500 mt-1">
                                    <span>Estoque atual: {selectedProduct.stock_quantity}</span>
                                    <span>Custo atual: R${(selectedProduct.cost_price || 0).toFixed(2)}</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-gray-400">Quantidade *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    className="bg-[#121212] border-[#262626] text-white text-xl font-bold text-center"
                                    placeholder="0"
                                    data-testid="movement-quantity-input"
                                />
                            </div>

                            {/* Unit Cost Field - Only for entries */}
                            {movementType === 'entrada' && (
                                <div className="space-y-2">
                                    <Label className="text-gray-400 flex items-center gap-2">
                                        <DollarSign className="w-4 h-4 text-rr-yellow-500" />
                                        Valor pago por unidade (opcional)
                                    </Label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={unitCost}
                                            onChange={(e) => setUnitCost(e.target.value)}
                                            className="bg-[#121212] border-[#262626] text-white pl-10"
                                            placeholder="0,00"
                                            data-testid="movement-unit-cost-input"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Informe o custo unitário desta compra para atualizar o custo médio do produto
                                    </p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label className="text-gray-400">Motivo (opcional)</Label>
                                <Select value={reason} onValueChange={setReason}>
                                    <SelectTrigger className="bg-[#121212] border-[#262626] text-white" data-testid="movement-reason-select">
                                        <SelectValue placeholder="Selecione um motivo" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121212] border-[#262626]">
                                        {movementType === 'entrada' ? (
                                            <>
                                                <SelectItem value="compra">Compra de fornecedor</SelectItem>
                                                <SelectItem value="devolucao">Devolução de cliente</SelectItem>
                                                <SelectItem value="ajuste">Ajuste de inventário</SelectItem>
                                            </>
                                        ) : (
                                            <>
                                                <SelectItem value="avaria">Avaria/Quebra</SelectItem>
                                                <SelectItem value="vencimento">Vencimento</SelectItem>
                                                <SelectItem value="ajuste">Ajuste de inventário</SelectItem>
                                                <SelectItem value="consumo">Consumo interno</SelectItem>
                                            </>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Summary */}
                            {quantity && (
                                <div className="p-4 bg-[#121212] rounded-md space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-400">Novo estoque:</span>
                                        <span className="text-xl font-bold text-rr-yellow-500">
                                            {movementType === 'entrada' 
                                                ? selectedProduct.stock_quantity + parseInt(quantity || 0)
                                                : selectedProduct.stock_quantity - parseInt(quantity || 0)
                                            }
                                        </span>
                                    </div>
                                    {movementType === 'entrada' && unitCost && parseFloat(unitCost) > 0 && (
                                        <>
                                            <div className="border-t border-[#262626] pt-2">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-400">Total desta compra:</span>
                                                    <span className="text-white font-medium">
                                                        R${(parseFloat(unitCost) * parseInt(quantity || 0)).toFixed(2)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between text-sm mt-1">
                                                    <span className="text-gray-400">Novo custo médio (estimado):</span>
                                                    <span className="text-emerald-500 font-medium">
                                                        R${(() => {
                                                            const currentStock = selectedProduct.stock_quantity;
                                                            const currentCost = selectedProduct.cost_price || 0;
                                                            const newQty = parseInt(quantity || 0);
                                                            const newCost = parseFloat(unitCost);
                                                            const totalQty = currentStock + newQty;
                                                            if (currentStock > 0 && currentCost > 0) {
                                                                return ((currentStock * currentCost + newQty * newCost) / totalQty).toFixed(2);
                                                            }
                                                            return newCost.toFixed(2);
                                                        })()}
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleMovement}
                            disabled={saving}
                            className={movementType === 'entrada' 
                                ? 'bg-emerald-500 hover:bg-emerald-400 text-white font-bold'
                                : 'bg-red-500 hover:bg-red-400 text-white font-bold'
                            }
                            data-testid="confirm-movement-btn"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default StockPage;
